import { ArrowRightOutlined } from "@ant-design/icons";
import type { GlobalToken } from "antd/lib/theme/interface";
import {
  ChangeColor,
  getChangeColor,
  getChangeArrowAngle,
} from "../CompareWithControls";
import { TimeUnit } from "../FormattingControls";
import { VZBLTooltip } from "../tooltips/VZBLTooltips";
interface ComparisonValue {
  current: number | null;
  comparison: number | null;
  delta: number | null;
  percentage: number | null;
  raw: string;
}

interface ComparisonCellProps {
  value: ComparisonValue;
  type: string;
  unit?: string;
  showChangeAs?: "value" | "percentage";
  showChangeValues?: boolean;
  changeColor?: ChangeColor;
  timeUnit?: TimeUnit;
  token?: GlobalToken;
  onClick?: (e: React.MouseEvent) => void;
}

export function ComparisonCell({
  value,
  type,
  unit,
  showChangeAs = "percentage",
  showChangeValues = true,
  changeColor = ChangeColor.TEXT,
  timeUnit,
  token,
  onClick,
}: ComparisonCellProps) {
  if (value === null) {
    return null;
  }

  // If we don't have valid values, show n/a
  if (
    value.current === null ||
    value.comparison === null ||
    value.delta === null ||
    value.percentage === null
  ) {
    return (
      <VZBLTooltip
        value={value}
        formattedValue="n/a"
        type="comparison"
        field={undefined}
        columnInfo={[]}
        currentColumn={undefined}
        metadata={{
          type,
          unit,
          isEntity: false,
        }}
      >
        <span style={{ fontWeight: 100, fontSize: 12 }}>-</span>
      </VZBLTooltip>
    );
  }

  const { current } = value;

  const { changeValue, formattedChangeValue, color, arrow } = prepareComparison(
    value,
    showChangeAs,
    changeColor,
    token || ({} as GlobalToken)
  );

  return (
    <VZBLTooltip
      value={value}
      formattedValue={formattedChangeValue}
      type="comparison"
      field={undefined}
      columnInfo={[]}
      currentColumn={undefined}
      metadata={{
        type,
        unit,
        isEntity: false,
      }}
    >
      <span
        style={{
          fontWeight: 400,
          fontSize: 12,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={onClick}
      >
        {/* Show original value if showChangeValues is true */}
        {showChangeValues && (
          <span style={{ color: token?.colorTextSecondary }}>
            {current}
            {unit}
          </span>
        )}

        {/* Show the comparison indicator with appropriate styling */}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            color: color || "inherit",
            backgroundColor: color ? `${color}11` : "inherit",
            borderRadius: 3,
            padding: "0 3px",
          }}
        >
          {arrow}
          <span>
            {Number(changeValue) > 0
              ? `+${formattedChangeValue}`
              : formattedChangeValue}
            {showChangeAs === "percentage" ? "%" : unit || ""}
          </span>
        </span>
      </span>
    </VZBLTooltip>
  );
}

//TODO this is temparary while I refactor the cell-formatters pattern to use the same pattern.
export function prepareComparison(
  value: ComparisonValue,
  showChangeAs: "value" | "percentage",
  changeColor: ChangeColor,
  token: GlobalToken
) {
  const { current, comparison, delta, percentage } = value;
  const changeValue = showChangeAs === "percentage" ? percentage : delta;
  const formattedChangeValue =
    changeValue === null ? "0" : changeValue.toFixed(2);
  const angle = delta === null ? 0 : getChangeArrowAngle(delta);
  const arrow = angle === 0 ? null : <ArrowRightOutlined rotate={angle} />;
  const color =
    token && changeColor === ChangeColor.TEXT && delta === null
      ? undefined
      : delta === null
      ? undefined
      : getChangeColor(token, changeColor, delta);

  return {
    changeValue,
    formattedChangeValue,
    color,
    arrow,
    angle,
  };
}
